/**
 * Base class for stock media providers
 * All stock media providers should extend this class
 */
class BaseStockProvider {
  constructor(config = {}) {
    this.name = config.name || 'Unknown Provider';
    this.apiKey = config.apiKey || null;
    this.baseUrl = config.baseUrl || '';
    this.mediaTypes = config.mediaTypes || ['image'];
    this.requiresAuth = config.requiresAuth || false;
  }

  /**
   * Check if a URL or file format is valid for After Effects
   * AE doesn't support: SVG, WebP, WebM, and some other formats
   * @param {string} url - URL or filename to check
   * @param {string} mediaType - Type of media (image, video, audio)
   * @returns {boolean} - True if format is valid for AE
   */
  isValidForAfterEffects(url, mediaType) {
    if (!url) return false;

    const urlLower = url.toLowerCase();
    
    // Invalid image formats for After Effects
    const invalidImageFormats = [
      '.svg',
      '.webp',
      'image/svg',
      'image/webp',
      '.avif',
      'image/avif',
      '.heic',
      'image/heic',
      '.heif',
      'image/heif'
    ];

    // Invalid video formats for After Effects
    const invalidVideoFormats = [
      '.webm',
      'video/webm',
      '.ogv',
      'video/ogg',
      '.mkv',
      'video/x-matroska'
    ];

    if (mediaType === 'image') {
      return !invalidImageFormats.some(format => urlLower.includes(format));
    } else if (mediaType === 'video') {
      return !invalidVideoFormats.some(format => urlLower.includes(format));
    }

    // For other media types (audio, etc.), allow by default
    return true;
  }

  /**
   * Filter out items with invalid formats for After Effects
   * @param {Array} items - Array of normalized media items
   * @returns {Array} - Filtered array with only valid formats
   */
  filterValidFormats(items) {
    return items.filter(item => {
      const isUrlValid = this.isValidForAfterEffects(item.url, item.type);
      const isDownloadUrlValid = this.isValidForAfterEffects(item.downloadUrl, item.type);
      const isThumbnailValid = this.isValidForAfterEffects(item.thumbnail, item.type);
      
      // Item is valid if main URL and download URL are valid
      // Thumbnail can be invalid (we can handle that separately)
      const isValid = isUrlValid && isDownloadUrlValid;
      
      if (!isValid) {
        console.log(`Filtered out ${item.provider} item due to invalid format:`, {
          name: item.name,
          url: item.url,
          type: item.type
        });
      }
      
      return isValid;
    });
  }

  /**
   * Search for media
   * @param {Object} params - Search parameters
   * @param {string} params.query - Search query
   * @param {string} params.mediaType - Type of media (image, video, audio)
   * @param {number} params.page - Page number
   * @param {number} params.perPage - Items per page
   * @returns {Promise<Object>} - Search results with data and pagination info
   */
  async search(params) {
    throw new Error('search() must be implemented by provider');
  }

  /**
   * Get popular/trending media
   * @param {Object} params - Parameters
   * @param {string} params.mediaType - Type of media
   * @param {number} params.page - Page number
   * @param {number} params.perPage - Items per page
   * @returns {Promise<Object>} - Results with data and pagination info
   */
  async getPopular(params) {
    throw new Error('getPopular() must be implemented by provider');
  }

  /**
   * Normalize media item to common format
   * @param {Object} item - Raw item from provider API
   * @param {string} mediaType - Type of media
   * @returns {Object} - Normalized media item
   */
  normalizeItem(item, mediaType) {
    throw new Error('normalizeItem() must be implemented by provider');
  }

  /**
   * Check if provider supports a media type
   * @param {string} mediaType - Type to check
   * @returns {boolean}
   */
  supportsMediaType(mediaType) {
    return this.mediaTypes.includes(mediaType);
  }

  /**
   * Get provider info
   * @returns {Object}
   */
  getInfo() {
    return {
      name: this.name,
      mediaTypes: this.mediaTypes,
      requiresAuth: this.requiresAuth,
    };
  }
}

export default BaseStockProvider;